import type { ColumnMapping, ImportBatchRow } from '@gwc/shared';
import { SAMPLE_IMPORT_COLUMNS, SAMPLE_IMPORT_CSV, SAMPLE_IMPORT_FILENAME } from '@gwc/shared';
import { api, apiUrl } from '../lib/api';

export interface StagingRowView {
  id: string;
  rowRef: string | null;
  status: string;
  normalized: Record<string, unknown> | null;
  errors: unknown[] | null;
  resolvedEntities: Record<string, unknown> | null;
  contentHash: string | null;
}

export interface PromoteReport {
  eventsCreated: number;
  eventsUpdated: number;
  catalogCreated: number;
  winesCreated: number;
  winesUpdated: number;
  scoresCreated: number;
  scoresUpdated: number;
  peopleCreated: number;
  rowsPromoted: number;
  errors: string[];
}

export const importsApi = {
  list: () => api.get<{ items: ImportBatchRow[] }>('/imports/files').then((r) => r.items),
  get: (id: string) => api.get<{ batch: ImportBatchRow }>(`/imports/files/${id}`).then((r) => r.batch),
  preview: (id: string) =>
    api.get<{ headers: string[]; sheets: string[] }>(`/imports/files/${id}/preview`),
  staging: (id: string) =>
    api.get<{ items: StagingRowView[]; total: number }>(`/imports/files/${id}/staging`),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<ImportBatchRow>('/imports/files', form);
  },
  remove: (id: string) => api.del<{ ok: true }>(`/imports/files/${id}`),
  parse: (id: string, body?: { mapping?: ColumnMapping; sheetName?: string }) =>
    api.post<{ rowCount: number; mapping: ColumnMapping; headers: string[] }>(
      `/imports/files/${id}/parse`,
      body ?? {},
    ),
  validate: (id: string) =>
    api.post<{ valid: number; invalid: number; total: number }>(`/imports/files/${id}/validate`),
  resolve: (id: string) =>
    api.post<{ resolved: number; needsReview: number }>(`/imports/files/${id}/resolve`),
  approve: (id: string, rowIds?: string[]) =>
    api.post<{ approved: number }>(`/imports/files/${id}/approve`, rowIds ? { rowIds } : {}),
  promote: (id: string, dryRun = false) =>
    api.post<{ dryRun: boolean; report: PromoteReport }>(
      `/imports/files/${id}/promote${dryRun ? '?dryRun=true' : ''}`,
    ),
  rollback: (id: string) => api.post<{ deleted: number }>(`/imports/files/${id}/rollback`),
  downloadUrl: (id: string) => apiUrl(`/imports/files/${id}/download`),
  sampleDownloadUrl: () => apiUrl('/imports/sample.csv'),
};

export function downloadSampleImportCsv(): void {
  const blob = new Blob([SAMPLE_IMPORT_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = SAMPLE_IMPORT_FILENAME;
  anchor.click();
  URL.revokeObjectURL(url);
}

export { SAMPLE_IMPORT_COLUMNS, SAMPLE_IMPORT_FILENAME };
